import { useEffect, useState, type ReactNode } from "react";
import { useQuoteCopy } from "../i18n/QuoteCopyContext";
import { DocumentIcon, SparklesIcon } from "./icons";

const STEP_KEYS = ["qf.analyzeStep1", "qf.analyzeStep2", "qf.analyzeStep3"] as const;

export class AnalysisDocument {
  constructor(private readonly file: File) {}

  name(): string {
    return this.file.name;
  }

  kind(): "image" | "pdf" {
    if (this.file.type.startsWith("image/")) return "image";
    return "pdf";
  }

  extension(): string {
    const dot = this.file.name.lastIndexOf(".");
    if (dot <= 0 || dot === this.file.name.length - 1) {
      return this.kind() === "pdf" ? "PDF" : "";
    }
    return this.file.name.slice(dot + 1).toUpperCase();
  }

  key(index: number): string {
    return `${index}:${this.file.name}:${this.file.size}:${this.file.lastModified}`;
  }
}

function useImagePreviews(files: readonly File[]): ReadonlyMap<string, string> {
  const [urls, setUrls] = useState<ReadonlyMap<string, string>>(new Map());

  useEffect(() => {
    const next = new Map<string, string>();
    const created: string[] = [];
    files.forEach((file, index) => {
      const doc = new AnalysisDocument(file);
      if (doc.kind() !== "image") return;
      const url = URL.createObjectURL(file);
      created.push(url);
      next.set(doc.key(index), url);
    });
    setUrls(next);
    return () => {
      created.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [files]);

  return urls;
}

function AnalysisFileCard({ file, previewUrl }: { file: File; previewUrl?: string }) {
  const doc = new AnalysisDocument(file);
  const [broken, setBroken] = useState(false);
  const showImage = doc.kind() === "image" && Boolean(previewUrl) && !broken;

  return (
    <article className="qf-doc">
      <div className="qf-doc-sheet">
        {showImage ? (
          <img
            src={previewUrl}
            alt={doc.name()}
            onError={() => setBroken(true)}
          />
        ) : (
          <div className="qf-doc-file">
            <DocumentIcon size={28} />
            {doc.extension() && <span className="qf-doc-ext">{doc.extension()}</span>}
          </div>
        )}
        <span className="qf-doc-scan" aria-hidden="true" />
      </div>
      <div className="qf-doc-name" title={doc.name()}>
        {doc.name()}
      </div>
    </article>
  );
}

interface Props {
  files: readonly File[];
  preview?: ReactNode;
}

export function DocumentAnalysisPanel({ files, preview }: Props) {
  const copy = useQuoteCopy();
  const previews = useImagePreviews(files);

  return (
    <div className="qf-analyze" role="status" aria-live="polite" aria-busy="true">
      {(files.length > 0 || preview) && (
        <div className="qf-analyze-docs">
          {preview && (
            <article className="qf-doc">
              <div className="qf-doc-sheet">
                {preview}
                <span className="qf-doc-scan" aria-hidden="true" />
              </div>
            </article>
          )}
          {files.map((file, index) => {
            const doc = new AnalysisDocument(file);
            return (
              <AnalysisFileCard
                key={doc.key(index)}
                file={file}
                previewUrl={previews.get(doc.key(index))}
              />
            );
          })}
        </div>
      )}
      <span className="qf-analyze-badge" aria-hidden="true">
        <SparklesIcon size={22} />
      </span>
      <p className="qf-analyze-title">{copy.t("qf.analyzeTitle")}</p>
      <p className="qf-analyze-sub">{copy.t("qf.analyzeSub")}</p>
      <ul className="qf-analyze-steps">
        {STEP_KEYS.map((key) => (
          <li key={key}>
            <span className="qf-analyze-dot" aria-hidden="true" />
            {copy.t(key)}
          </li>
        ))}
      </ul>
    </div>
  );
}
